/**
 * Copyright Mántum All Rights Reserved.
 */
import { useCallback, useMemo } from 'react';
import {
    ArgumentAxis,
    Chart,
    CommonSeriesSettings,
    ConstantLine,
    Grid,
    Crosshair,
    Export,
    Point,
    Font,
    Label,
    Legend,
    Series as SeriesComponent,
    Title,
    Tooltip,
    ValueAxis,
    VisualRange
} from 'devextreme-react/chart';

import type { Limit, Series, Overlapping } from '../type';
import type { Palette } from 'devextreme/common/charts';

type PropTypes = {
    advanced?: {
        allowCrosshair?: boolean;
        allowExporting?: boolean;
        allowLegend?: boolean;
        allowTooltip?: boolean;
        entity?: string;
        overlapping?: Overlapping;
        refresh?: number;
        revalidate?: boolean;
    };
    dataSource?: unknown[];
    limit?: Limit;
    series?: Series;
    subtitle?: string;
    theme?: Palette;
    title?: string;
    type?: 'bar' | 'area' | 'line' | 'spline';
};

export default function Common(props: PropTypes) {
    // prettier-ignore
    const {
        advanced = {},
        series = []
    } = props;

    const {
        allowCrosshair = false,
        allowExporting = false,
        allowLegend = true,
        allowTooltip = false,
        overlapping = 'rotate'
    } = advanced;

    const onCustomizePoint = useCallback(
        (data: Record<string, unknown>) => {
            if (!props.limit || !('value' in data)) {
                return {};
            }

            const value = data.value;
            if (typeof value === 'number') {
                if (props.limit.max && value > props.limit.max.value) {
                    return {
                        color: props.limit.max.color,
                        hoverStyle: {
                            color: props.limit.max.color
                        }
                    };
                }

                if (props.limit.min && value < props.limit.min.value) {
                    return {
                        color: props.limit.min.color,
                        hoverStyle: {
                            color: props.limit.min.color
                        }
                    };
                }
            }

            return {};
        },
        [props.limit]
    );

    const isMargin = useMemo(() => {
        return (
            props.type !== 'area' &&
            props.type !== 'line' &&
            props.type !== 'spline'
        );
    }, [props.type]);

    return (
        <Chart
            customizePoint={onCustomizePoint}
            dataSource={props.dataSource}
            palette={props.theme}
        >
            <CommonSeriesSettings
                argumentField={advanced.entity}
                type={props.type}
            >
                <Point hoverMode='allArgumentPoints' />
            </CommonSeriesSettings>

            {series
                .filter((it) => {
                    return it.visible !== undefined && it.visible !== null
                        ? it.visible
                        : true;
                })
                .map((it) => (
                    <SeriesComponent
                        argumentField={it.field}
                        color={it.color}
                        key={it.value}
                        name={it.label}
                        valueField={it.value}
                    />
                ))}

            {props.limit && (
                <ValueAxis maxValueMargin={0.01}>
                    {props.limit.start && (
                        <VisualRange startValue={props.limit.start} />
                    )}

                    {props.limit.max && (
                        <ConstantLine
                            width={2}
                            value={props.limit.max.value}
                            color={props.limit.max.color}
                            dashStyle='dash'
                        >
                            <Label text={props.limit.max.title} />
                        </ConstantLine>
                    )}

                    {props.limit.min && (
                        <ConstantLine
                            width={2}
                            value={props.limit.min.value}
                            color={props.limit.min.color}
                            dashStyle='dash'
                        >
                            <Label text={props.limit.min.title} />
                        </ConstantLine>
                    )}
                </ValueAxis>
            )}

            {allowCrosshair && (
                <Crosshair
                    color='#949494'
                    dashStyle='dot'
                    enabled={true}
                    width={3}
                >
                    <Label visible={true} backgroundColor='#949494'>
                        <Font color='#fff' size={12} />
                    </Label>
                </Crosshair>
            )}

            <ArgumentAxis
                valueMarginsEnabled={isMargin}
                discreteAxisDivisionMode='crossLabels'
            >
                <Grid visible={allowCrosshair} />
                <Label wordWrap='none' overlappingBehavior={overlapping} />
            </ArgumentAxis>

            <Title text={props.title} subtitle={props.subtitle} />

            <Legend visible={allowLegend} />
            <Export enabled={allowExporting} />
            <Tooltip enabled={allowTooltip || allowCrosshair} />
        </Chart>
    );
}
